import {inject, Injectable} from '@angular/core';
import {FormWrapper} from "../libs/form/items/form.wrapper";
import {LoggingService} from "./logger/logging.service";
import {BehaviorSubject, Observable, of, Subscription} from "rxjs";
import {isDefined, isTrue, uniqueArray, unsubscribe} from "../libs/utils/epic.utils";
import {FormBuilder} from "@angular/forms";
import {FormStateEnum} from "../libs/form/items/form-state.enum";
import {FormSelectOption} from "../libs/form/items/form-select-option";
import {map} from "rxjs/operators";
import {I18nService} from "./i18n/i18n.service";
import {FormOptions} from "../libs/form/items/form-options";
import {VatRateService} from "./vat-rate.service";
import {CurrencyService} from "./currency.service";
import {BankAddressDto} from "../repositories/dto/bank-address.dto";
import {VehicleHistory} from "../libs/enum/vehicle-history";
import {SettingsService} from "./settings.service";
import {SettingKey} from "../base/setting-key.enum";
import {SettingDto} from "../repositories/dto/setting.dto";
import {PaymentMethod} from "../libs/enum/payment-method";
import {UnitService} from "./unit.service";
import {ItemUnit} from "../libs/enum/item-unit.enum";

export class LabelOptions {

  public billingAddress: boolean = false;
  public support: boolean = false;

  constructor(init?: Partial<LabelOptions> | undefined) {
    Object.assign(this, init);
  }
}

@Injectable({providedIn: 'root'})
export class FormService {

  private readonly log: LoggingService = inject(LoggingService);
  private readonly builder: FormBuilder = inject(FormBuilder);
  private readonly i18n: I18nService = inject(I18nService);
  private readonly vatRateService: VatRateService = inject(VatRateService);
  private readonly currencyService: CurrencyService = inject(CurrencyService);
  private readonly settingsService: SettingsService = inject(SettingsService);
  private readonly unitService: UnitService = inject(UnitService);

  private readonly hidden = '';
  private readonly none = 'default';
  private readonly success = 'success';
  private readonly working = 'warning';
  private readonly error = 'danger';
  private readonly onStateChangeEvent = new BehaviorSubject<string>(this.hidden);

  private wrapperMap = new Map<string, { wrapper: FormWrapper, subs: Subscription[] }>();
  private state: string = this.none;
  private wrapperState = new BehaviorSubject<FormStateEnum>(FormStateEnum.NONE);
  private emissionsTestDisabled: SettingDto | undefined;

  constructor() {
    this.settingsService.getOrgSettings()
      .pipe(map((ss) => ss.find((s) => s.key === SettingKey.ORG_CUSTOMER_VEHICLE_EMISSIONS_TEST_DISABLE)))
      .subscribe((s) => this.emissionsTestDisabled = s);
  }

  public getState(): string {
    return this.state;
  }

  public getWrapperState(): FormStateEnum {
    return this.wrapperState.value;
  }

  public getWrapperStateChange(): Observable<FormStateEnum> {
    return this.wrapperState.asObservable();
  }

  public onStateChange(): Observable<string> {
    return this.onStateChangeEvent.asObservable();
  }

  public hasWrapper(): boolean {
    return this.wrapperMap.size > 0;
  }

  /**
   * Use method for DEBUG purposes only !!!
   * The map is for internal service use only !!!
   */
  public getWrapperMap(): Map<string, { wrapper: FormWrapper, subs: Subscription[] }> {
    return this.wrapperMap;
  }

  public buildWrapper(id: string, options?: FormOptions): FormWrapper {
    const wrapper = new FormWrapper(id, this.log, this.builder, options);
    this.addForm(wrapper);
    return wrapper;
  }

  public destroyWrapper(wrapper: FormWrapper | FormWrapper[] | undefined): void {
    if (isDefined(wrapper)) {
      if (Array.isArray(wrapper)) {
        wrapper.forEach((w) => this.destroyWrapper(w));
      } else {
        wrapper.destroy();
        this.removeForm(wrapper);
        wrapper.getSubWrappers().forEach((w) => this.destroyWrapper(w));
      }
    }
  }

  public addForm(wrapper: FormWrapper): void {
    this.removeForm(wrapper);
    this.log.trace('FormService: add form with id "{}"', [wrapper.getId()]);
    if (this.wrapperMap.has(wrapper.getId())) {
      this.log.error('Form with id "{}" already exists', [wrapper.getId()]);
      this.removeForm(this.wrapperMap.get(wrapper.getId())?.wrapper);
    }
    const subs = [wrapper.onStateChange().subscribe(() => this.updateState())];
    this.wrapperMap.set(wrapper.getId(), {wrapper, subs});
    this.updateState();
  }

  public removeForm(wrapper: FormWrapper | undefined): void {
    if (isDefined(wrapper)) {
      const found = this.wrapperMap.has(wrapper.getId()) ? 1 : 0;
      this.log.trace('FormService: remove "{}" form with id "{}"', [found, wrapper.getId()]);
      if (found >= 1) {
        unsubscribe(this.wrapperMap.get(wrapper.getId())?.subs);
      }

      this.wrapperMap.delete(wrapper.getId());
      if (this.wrapperMap.size <= 0) {
        this.setState(this.hidden, FormStateEnum.EMPTY);
      }
      this.updateState();
    }
  }

  public saveAllForms(): void {
    if (this.hasWrapper()) {
      this.getWrapperMap().forEach((w) => {
        if (w.wrapper.isInvalid()) {
          w.wrapper.markAllInvalidAsTouched();
        } else if (w.wrapper.getState() === FormStateEnum.CHANGED) {
          w.wrapper.submit();
        }
      });
    }
  }

  public buildLanguageSelectOptions(): Observable<FormSelectOption[]> {
    return of(this.i18n.getLanguageCodes().map((l) => new FormSelectOption('language.' + l, l)));
  }

  public buildCountrySelectOptions(): Observable<FormSelectOption[]> {
    return this.i18n.getCountries().pipe(map((kvs) => kvs.map((kv) => new FormSelectOption(kv.value!, kv.key).useName())));
  }

  public buildTimezoneSelectOptions(): Observable<FormSelectOption[]> {
    return this.i18n.getTimezones().pipe(map((kvs) => kvs.map((kv) => new FormSelectOption(kv.value!, kv.key).useName())));
  }

  public buildAcademicDegreeSelectOptions(data?: Observable<string[]>): Observable<FormSelectOption[]> {
    const academicDegrees: FormSelectOption[] = [
      new FormSelectOption('academicDegree.doctor', 'academicDegree.doctor'),
      new FormSelectOption('academicDegree.doctorOfMedicine', 'academicDegree.doctorOfMedicine'),
      new FormSelectOption('academicDegree.professor', 'academicDegree.professor'),
      new FormSelectOption('academicDegree.graduateEngineer', 'academicDegree.graduateEngineer'),
    ];
    const names = academicDegrees.map((i) => i.name);
    return (isDefined(data) ? data : of([])).pipe(
      map((ad) => ad.filter((i) => !names.includes(i))),
      map((ad) => ad.map((i) => new FormSelectOption(i, i).useName())),
      map((ad) => academicDegrees.concat(ad)),
      map((ad) => uniqueArray<FormSelectOption>(ad, 'name'))
    );
  }

  public buildSalutationSelectOptions(data?: Observable<string[]>): Observable<FormSelectOption[]> {
    const salutations: FormSelectOption[] = [
      new FormSelectOption('salutation.sir', 'salutation.sir'),
      new FormSelectOption('salutation.madam', 'salutation.madam'),
    ];
    const names = salutations.map((i) => i.name);
    return (isDefined(data) ? data : of([])).pipe(
      map((sa) => sa.filter((i) => !names.includes(i))),
      map((sa) => sa.map((i) => new FormSelectOption(i, i).useName())),
      map((sa) => salutations.concat(sa)),
      map((sa) => uniqueArray<FormSelectOption>(sa, 'name')),
    );
  }

  public buildSectorSelectOptions(filterCountryCode?: string): Observable<FormSelectOption[]> {
    return this.i18n.getSectors(filterCountryCode)
      .pipe(map((kvs) => kvs
        .map((kv) => {
          const name = this.i18n.translate(kv.key);
          return new FormSelectOption(name, kv.value!).useName();
        }).sort((a, b) => {
          if (a.value === 'OTHER') {
            return 1;
          }
          if (b.value === 'OTHER') {
            return -1;
          }
          return a.name.localeCompare(b.name);
        })
      ));
  }

  public buildBankAddressSelectOptions(bankAddresses: BankAddressDto[]): FormSelectOption[] {
    if (isDefined(bankAddresses) && bankAddresses.length > 0) {
      return bankAddresses.map((b) => {
        const name = b.bankName + ' - ' + b.maskIban;
        return new FormSelectOption(name, b.id).useName();
      });
    }
    return [];
  }

  public buildOccurrenceLabelSelectOptions(data?: Observable<string[]>): Observable<FormSelectOption[]> {
    const labels: FormSelectOption[] = [];
    return this.buildLabelFormSelectOptions(labels, data);
  }

  public buildLabelSelectOptions(data?: Observable<string[]>, options?: LabelOptions): Observable<FormSelectOption[]> {
    const labels: FormSelectOption[] = [];
    if (isDefined(options) && options.billingAddress) {
      labels.push(new FormSelectOption('label.billingAddress', 'label.billingAddress'));
    }
    labels.push(new FormSelectOption('label.private', 'label.private'));
    labels.push(new FormSelectOption('label.business', 'label.business'));
    if (isDefined(options) && options.support) {
      labels.push(new FormSelectOption('label.support', 'label.support'));
    }
    return this.buildLabelFormSelectOptions(labels, data);
  }

  private buildLabelFormSelectOptions(labels: FormSelectOption[], data?: Observable<string[]>): Observable<FormSelectOption[]> {
    const names = labels.map((i) => i.name);
    return (isDefined(data) ? data : of([])).pipe(
      map((ls) => ls.filter((i) => !names.includes(i))),
      map((ls) => ls.map((l) => {
        const option = new FormSelectOption(l, l);
        return l.startsWith('label.') ? option : option.useName();
      })),
      map((ls) => labels.concat(ls)),
      map((ls) => uniqueArray<FormSelectOption>(ls, 'name')),
    );
  }

  public buildVehicleHistorySelectOptionsShort(): Observable<FormSelectOption[]> {
    return this.buildVehicleHistorySelectOptions()
      .pipe(map((os) => os.map(o => new FormSelectOption(o.name + '.short', o.value))));
  }

  public buildVehicleHistorySelectOptions(all: boolean = false): Observable<FormSelectOption[]> {
    const excludeEmissionsTest: boolean = this.emissionsTestDisabled?.isTrue() ?? false;
    const entries = Object.keys(VehicleHistory)
      .filter((h) => isTrue(all) || h !== VehicleHistory.RECOMMENDED_REPAIR)
      .filter((h) => {
        if (isTrue(all)) {
          return true;
        } else if (isTrue(excludeEmissionsTest)) {
          return h !== VehicleHistory.EMISSIONS_TEST;
        }
        return true;
      })
      .map((c) => new FormSelectOption('vehicle.history.' + c.toLowerCase(), c));
    return of(entries);
  }

  public buildVatRateSelectOptions(): Observable<FormSelectOption[]> {
    return this.vatRateService.buildSelectOptions(this.vatRateService.getCurrent());
  }

  public buildVatRateSelectOptionsByDate(date: Date | undefined): Observable<FormSelectOption[]> {
    if (isDefined(date)) {
      return this.vatRateService.buildSelectOptions(this.vatRateService.getByDate(date));
    }
    return this.buildVatRateSelectOptions();
  }

  public buildCurrentCurrencySelectOptions(): Observable<FormSelectOption[]> {
    return this.currencyService.getCurrents()
      .pipe(map((cs) => cs.map((c) => new FormSelectOption(c.code, c.code).useName())));
  }

  // noinspection JSUnusedGlobalSymbols
  public buildAllCurrencySelectOptions(): Observable<FormSelectOption[]> {
    return this.currencyService.getAll()
      .pipe(map((cs) => cs.map((c) => new FormSelectOption(c.code, c.code).useName())));
  }

  public buildSupportedCurrencySelectOptions(): Observable<FormSelectOption[]> {
    return of(['EUR', 'USD', 'GEL'].map((code) => new FormSelectOption(code, code).useName()));
  }

  public buildFractionalDigitsSelectOptions(): Observable<FormSelectOption[]> {
    return of(['2', '3', '4', '5'].map((d) => new FormSelectOption(d, d).useName()));
  }

  public getPaymentMethodsForView(): PaymentMethod[] {
    return [PaymentMethod.BANK_TRANSFER, PaymentMethod.CASH, PaymentMethod.DISCOUNT, PaymentMethod.GRATUITY];
  }

  public buildPaymentMethodSelectOptions(): FormSelectOption[] {
    const methods = this.getPaymentMethodsForView();
    return methods.map((m) => new FormSelectOption('payment.method.' + m.toLowerCase(), m));
  }

  public buildUnitSelectOptions(current?: ItemUnit): FormSelectOption[] {
    return this.unitService.getItemUnits(current)
      .filter((e) => isDefined(e))
      .map((e) => String(e))
      .map((e) => {
        const fieldName = 'unit.' + e.toLowerCase();
        return new FormSelectOption(fieldName, e.toUpperCase());
      });
  }

  private updateState(): void {
    let state = -1;
    let wrapperState: FormStateEnum = FormStateEnum.EMPTY;
    this.wrapperMap.forEach((value) => {
      if (!value.wrapper.isViewOnly()) {
        const priority = this.getStatePriority(value.wrapper.getState());
        if (priority > state) {
          state = priority;
          wrapperState = value.wrapper.getState();
        }
      }
    });
    this.setState(this.getStateByPriority(state), wrapperState);
  }

  private setState(state: string, wrapperState: FormStateEnum): void {
    this.log.trace('FormService: change state to: {}', [state]);
    this.state = state;
    this.wrapperState.next(wrapperState);
    this.onStateChangeEvent.next(state);
  }

  private getStatePriority(state: FormStateEnum): number {
    return state.valueOf();
  }

  private getStateByPriority(priority: number): string {
    switch (priority) {
      default:
      case -1:
        return this.hidden;
      case 0:
        return this.none;
      case 1:
        return this.success;
      case 2:
      case 3:
        return this.working;
      case 4:
        return this.error;
    }
  }
}
